// 포함된 기능

// ✅ 시작일 / 종료일 input
// ✅ readonly 클릭 시 달력 open
// ✅ 이전 / 다음 달 이동
// ✅ defaultValue 반영
// ✅ 시작일 / 종료일 선택
// ✅ 범위 background 표시
// ✅ hover range preview
// ✅ start > end 자동 swap
// ✅ today 표시
// ✅ 토요일 / 일요일 날짜 색상 표시
// ✅ 요일 language 지원 (ko / en)
// ✅ year picker (±5년)
// ✅ month picker
// ✅ outside click close
// ✅ 부모 컴포넌트에서 값 읽기 (onChange)
// ✅ handlers.ts 로직 분리
// ✅ tsx는 html 중심 작성
// ✅ 성능 (useMemo)

import React, { useState, useMemo, useEffect, useRef } from "react";

import {
  weekNames,
  months,
  generateCalendar,
  prevMonth,
  nextMonth,
  isSameDay,
  isInRange,
  getYearRange,
  swapIfNeeded,
  isSunday,
  isSaturday,
  DateRangePickerProps,
  ViewMode,
  parseYYYYMMDD,
  Language,
} from "./handlers";

import "./datePicker.scss";
import i18n from "locales/i18n";

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  label = "조회기간",
  defaultStart,
  defaultEnd,
  startNm = "sDt",
  endNm = "eDt",
  language = "ko",
  onChange,
}) => {
  // const language = i18n.language as Language;
  const wrapperRef = useRef<HTMLDivElement>(null);

  const start = parseYYYYMMDD(defaultStart ?? "");
  const end = parseYYYYMMDD(defaultEnd ?? "");

  const [startDate, setStartDate] = useState<Date | null>(start);
  const [endDate, setEndDate] = useState<Date | null>(end);

  const [hoverDate, setHoverDate] = useState<Date | null>(null);

  const [year, setYear] = useState(start.getFullYear());
  const [month, setMonth] = useState(start.getMonth());

  const [view, setView] = useState<ViewMode>("date");

  const [open, setOpen] = useState(false);
  const [selecting, setSelecting] = useState<"start" | "end">("start");

  const today = new Date();

  const calendar = useMemo(() => generateCalendar(year, month), [year, month]);

  const yearRange = useMemo(() => getYearRange(year), [year]);

  const prev = () => {
    const v = prevMonth(year, month);
    setYear(v.year);
    setMonth(v.month);
  };

  const next = () => {
    const v = nextMonth(year, month);
    setYear(v.year);
    setMonth(v.month);
  };

  const selectDate = (day: number) => {
    const date = new Date(year, month, day);

    if (selecting === "start") {
      setStartDate(date);
      setEndDate(null);
      setSelecting("end");
    } else {
      const result = swapIfNeeded(startDate, date);

      setStartDate(result.startDate);
      setEndDate(result.endDate);

      setSelecting("start");
    }
  };

  const previewRange = (date: Date) => {
    if (!startDate || endDate) return false;
    if (!hoverDate) return false;

    if (date > startDate) return date <= hoverDate;

    if (date < startDate) return date >= hoverDate;

    return false;
  };

  useEffect(() => {
    const start = parseYYYYMMDD(defaultStart ?? "");
    const end = parseYYYYMMDD(defaultEnd ?? "");
    setStartDate(start);
    setEndDate(end);
  }, [defaultStart, defaultEnd]);

  useEffect(() => {
    // const start = Intl.DateTimeFormat("en-CA", {
    //   timeZone: "Asia/Seoul",
    //   year: "numeric",
    //   month: "2-digit",
    //   day: "2-digit",
    // }).format(startDate ?? undefined);

    // const end = Intl.DateTimeFormat("en-CA", {
    //   timeZone: "Asia/Seoul",
    //   year: "numeric",
    //   month: "2-digit",
    //   day: "2-digit",
    // }).format(endDate ?? undefined);
    const start = startDate
      ?.toLocaleDateString("en-CA", { timeZone: "Asia/Seoul" })
      .replace(/-/g, "") as string;
    const end = endDate
      ?.toLocaleDateString("en-CA", { timeZone: "Asia/Seoul" })
      .replace(/-/g, "") as string;
    onChange?.([start, end]);
    // onChange?.({ start, end });
  }, [startDate, endDate]);

  useEffect(() => {
    const handle = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handle);

    return () => {
      document.removeEventListener("mousedown", handle);
    };
  }, []);

  return (
    <div ref={wrapperRef} className="date-range-picker relative w-80 flex">
      <label htmlFor="w-full flex-1">{label}</label>
      <div className="flex gap-1 flex-1">
        <input
          readOnly
          name={startNm}
          value={startDate?.toLocaleDateString() ?? ""}
          onClick={() => {
            setSelecting("start");
            setOpen(!open);
          }}
          className="border p-1 w-full"
        />
        <span>-</span>
        <input
          readOnly
          name={endNm}
          value={endDate?.toLocaleDateString() ?? ""}
          onClick={() => {
            setSelecting("end");
            setOpen(!open);
          }}
          className="border p-1 w-full"
        />
      </div>

      {open && (
        <div className="calendar bg-white shadow-lg p-2">
          <div className="flex justify-between mb-3">
            <button onClick={prev}>◀</button>

            <div
              className="cursor-pointer font-bold"
              onClick={() => setView("year")}
            >
              {year}.{month + 1}
            </div>

            <button onClick={next}>▶</button>
          </div>

          {view === "year" && (
            <div className="grid grid-cols-4 gap-2">
              {yearRange.map((y) => (
                <div
                  key={y}
                  onClick={() => {
                    setYear(y);
                    setView("month");
                  }}
                  className="year-item"
                >
                  {y}
                </div>
              ))}
            </div>
          )}

          {view === "month" && (
            <div className="grid grid-cols-4 gap-2">
              {months.map((m, i) => (
                <div
                  key={m}
                  onClick={() => {
                    setMonth(i);
                    setView("date");
                  }}
                  className="month-item"
                >
                  {m}
                </div>
              ))}
            </div>
          )}

          {view === "date" && (
            <>
              <div className="grid grid-cols-7 text-center text-xs mb-2 font-bold">
                {weekNames[language].map((d, i) => (
                  <div
                    key={i}
                    className={i === 0 ? "sun" : i === 6 ? "sat" : ""}
                  >
                    {d}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-1">
                {calendar.map((day, i) => {
                  if (!day) return <div key={i} />;

                  const date = new Date(year, month, day);

                  const selected =
                    isSameDay(date, startDate) || isSameDay(date, endDate);

                  const inRange = isInRange(date, startDate, endDate);

                  const preview = previewRange(date);

                  const todayMark = isSameDay(date, today);

                  const sun = isSunday(date);
                  const sat = isSaturday(date);

                  return (
                    <div
                      key={i}
                      onMouseEnter={() => setHoverDate(date)}
                      onClick={() => selectDate(day)}
                      className={`day
                      ${selected ? "selected" : ""}
                      ${inRange ? "range" : ""}
                      ${preview ? "preview" : ""}
                      ${todayMark ? "today" : ""}
                      ${sun ? "sun-day" : ""}
                      ${sat ? "sat-day" : ""}
                      `}
                    >
                      {day}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
